import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Wallet, PackageCheck, Percent, TrendingDown, Timer, Users, BarChart3 } from "lucide-react";
import StatsReminders from "../components/stats/StatsReminders";
import api from "../lib/api";
import { formatKrWhole } from "../lib/format";
import { KpiCard } from "../components/KpiStrip";
import StatsPeriod from "../components/stats/StatsPeriod";
import StatsChart from "../components/stats/StatsChart";
import StatsHeat from "../components/stats/StatsHeat";
import StatsVariants from "../components/stats/StatsVariants";
import StatsOffers from "../components/stats/StatsOffers";
import StatsTop from "../components/stats/StatsTop";
import { duration } from "../components/stats/util";

function Trend({ value, unit = "%", invert, testid }) {
  if (value == null) return <span className="kc-trend flat" data-testid={testid}>— vs. forrige periode</span>;
  const good = invert ? value < 0 : value > 0;
  const cls = value === 0 ? "flat" : good ? "up" : "down";
  return (
    <span className={`kc-trend ${cls}`} data-testid={testid}>
      <span aria-hidden="true">{value === 0 ? "■" : value > 0 ? "▲" : "▼"}</span> {Math.abs(value)} {unit}
      <span className="kc-trend-vs"> vs. forrige periode</span>
    </span>
  );
}

function Skeleton() {
  return (
    <div data-testid="stats-skeleton" aria-busy="true">
      <section className="kstrip st-kpis">
        {Array.from({ length: 6 }, (_, i) => (
          <div className="kcard skel" key={i}><span className="sk sk-icon" /><span className="sk sk-num" /><span className="sk sk-line" /></div>
        ))}
      </section>
      <div className="panel"><span className="sk" style={{ height: 240 }} /></div>
    </div>
  );
}

export default function Stats() {
  const [sp, setSp] = useSearchParams();
  const from = sp.get("from"), to = sp.get("to");
  const preset = from && to ? "custom" : sp.get("p") || "30";
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setD(null); setErr(null);
    const params = preset === "custom" ? { from, to } : { p: preset };
    api.get("/stats", { params }).then((r) => setD(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Kunne ikke hente statistik"));
  }, [preset, from, to]);

  const t = d?.total, tr = d?.trend;
  const noData = d && !d.per.length && !d.comments_count;
  return (
    <>
      <div className="page-head st-head">
        <div>
          <h1>Statistik</h1>
          <p data-testid="stats-range-label">{d ? d.label : "\u00a0"}</p>
        </div>
      </div>
      <StatsPeriod key={`${from}${to}`} preset={preset} from={from || d?.from} to={to || d?.to}
        onPreset={(p) => setSp(p === "30" ? {} : { p })} onCustom={(f, tt) => setSp({ from: f, to: tt })} />
      {err && <div className="st-empty" role="alert" data-testid="stats-error">{err}</div>}
      {!d && !err && <Skeleton />}
      {d && (
        <>
          <section className="kstrip st-kpis" data-testid="stats-kpis">
            <KpiCard icon={Wallet} value={formatKrWhole(t.revenue)} unit="kr." label="omsat" testid="stats-kpi-revenue"
              sub={<Trend value={tr.revenue} testid="stats-trend-revenue" />} />
            <KpiCard icon={PackageCheck} value={t.picked} unit="stk." label="udleveret" testid="stats-kpi-picked"
              sub={<Trend value={tr.picked} testid="stats-trend-picked" />} />
            <KpiCard icon={Percent} tone={d.rate == null ? "green" : d.rate >= 80 ? "rate-good" : d.rate >= 60 ? "rate-ok" : "rate-low"}
              value={d.rate == null ? "—" : d.rate} unit={d.rate == null ? null : "%"} label="afhentningsrate" testid="stats-kpi-rate"
              sub={<Trend value={tr.rate_pp} unit="pct.-point" testid="stats-trend-rate" />} />
            <KpiCard icon={TrendingDown} tone="amber" value={formatKrWhole(t.lost)} unit="kr." label="tabt på udeblivelser" testid="stats-kpi-lost"
              sub={<Trend value={tr.lost} invert testid="stats-trend-lost" />} />
            <KpiCard icon={Timer} value={duration(d.avg_tts)} label="gns. tid til udsolgt" testid="stats-kpi-tts"
              sub={<Trend value={tr.avg_tts} invert testid="stats-trend-tts" />} />
            <KpiCard icon={Users} value={d.unique_customers} label="forskellige kunder" testid="stats-kpi-customers"
              sub={<Trend value={tr.unique_customers} testid="stats-trend-customers" />} />
          </section>
          {d.prev.has_data && <p className="st-prev" data-testid="stats-prev-label">Sammenlignet med {d.prev_label}</p>}
          <StatsReminders r={d.comment_reminders} />
          {noData ? (
            <div className="st-empty" data-testid="stats-empty">
              <BarChart3 size={26} aria-hidden="true" />
              <b>Ingen data i perioden</b>
              <p>Der er ingen afsluttede eller aktive tilbud med afhentning i {d.label}. Prøv en længere periode.</p>
            </div>
          ) : (
            <>
              <div className="st-grid">
                <StatsChart data={d.chart} gran={d.gran} />
                <StatsHeat heat={d.heat} count={d.comments_count} />
              </div>
              <StatsOffers per={d.per} saved={t.saved} />
              <div className="st-grid">
                <StatsVariants data={d.variants} />
                <StatsTop top={d.top} />
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}
